import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClients } from '../hooks/useClients';
import { useExercises } from '../hooks/useExercises';
import { useWorkouts } from '../hooks/useWorkouts';
import CreateExerciseModal from '../components/CreateExerciseModal';
import CreateClientModal from '../components/CreateClientModal';
import CreateWorkoutModal from '../components/CreateWorkoutModal';
import Navigation from '../components/Navigation';
import WeeklyCalendar from '../components/WeeklyCalendar';
import { ExerciseCreate, ClientCreate, WorkoutCreate, Workout } from '../types';
import { ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { formatPhoneDisplay } from '../utils/formatPhone';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { trainer, logout } = useAuth();
  const { clients = [], isLoading: isClientsLoading, createClientWithCallback, isCreateLoading: isCreateClientLoading } = useClients();
  const { exercises = [], isLoading: isExercisesLoading, createExercise, isCreateLoading } = useExercises({ limit: 1000 });
  const { workouts = [], isLoading: isWorkoutsLoading, createWorkoutWithCallback, isCreateLoading: isCreateWorkoutLoading } = useWorkouts();
  
  // Состояние для модальных окон
  const [isCreateExerciseModalOpen, setIsCreateExerciseModalOpen] = useState(false);
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [isCreateWorkoutModalOpen, setIsCreateWorkoutModalOpen] = useState(false);

  // Подсчитываем статистику
  const totalClients = clients?.length || 0;
  const totalExercises = exercises?.length || 0;
  const totalWorkouts = workouts?.length || 0;
  const completedWorkouts = workouts?.filter(w => w.is_completed).length || 0;

  const isLoading = isClientsLoading || isExercisesLoading || isWorkoutsLoading;

  // Обработчик создания упражнения
  const handleCreateExercise = (data: ExerciseCreate) => {
    createExercise(data);
    setIsCreateExerciseModalOpen(false);
  };

  // Обработчик создания клиента
  const handleCreateClient = (data: ClientCreate) => {
    createClientWithCallback(data, () => {
      setIsCreateClientModalOpen(false);
      navigate('/clients');
    });
  };

  // Обработчик создания тренировки
  const handleCreateWorkout = (data: WorkoutCreate) => {
    console.log('Dashboard: Creating workout with data:', data);
    createWorkoutWithCallback(data, (workout) => {
      console.log('Dashboard: Workout created successfully:', workout);
      setIsCreateWorkoutModalOpen(false);
      setPreSelectedWorkoutDate(undefined);
      navigate(`/workouts/${workout.id}`);
    });
  };

  const handleCloseCreateWorkoutModal = () => {
    setIsCreateWorkoutModalOpen(false);
    setPreSelectedWorkoutDate(undefined);
  };

  // Обработчики кликов по плашкам
  const handleClientsClick = () => {
    navigate('/clients');
  };

  const handleExercisesClick = () => {
    navigate('/exercises');
  };

  const handleWorkoutsClick = () => {
    navigate('/workouts');
  };

  const handleCompletedWorkoutsClick = () => {
    navigate('/workouts?status=completed');
  };

  // Обработчик клика по тренировке в календаре
  const handleWorkoutClick = (workout: Workout) => {
    navigate(`/workouts/${workout.id}`);
  };

  // Обработчик клика по множественным тренировкам
  const handleMultipleWorkoutsClick = (date: Date, hour: number) => {
    // Переходим на страницу тренировок с фильтром по дате и часу
    const formattedDate = format(date, 'yyyy-MM-dd');
    navigate(`/workouts?date=${formattedDate}&hour=${hour}`);
  };

  // Обработчик клика по пустому часу
  const handleHourClick = (date: Date, hour: number) => {
    // Создаем дату с указанным часом
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour, 0, 0, 0);
    
    // Устанавливаем предустановленную дату
    setPreSelectedWorkoutDate(selectedDateTime);
    
    // Открываем модальное окно создания тренировки
    setIsCreateWorkoutModalOpen(true);
  };

  // Состояние для предустановленной даты
  const [preSelectedWorkoutDate, setPreSelectedWorkoutDate] = useState<Date | undefined>();



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Навигационная панель */}
      <Navigation currentPage="dashboard" />

      {/* Основной контент */}
      <main className="max-w-7xl mx-auto py-2 sm:px-6 lg:px-8">
        <div className="px-4 py-1 sm:px-0">

          {/* Календарь тренировок */}
          <div className="mb-8">
            <WeeklyCalendar
              workouts={workouts}
              isLoading={isWorkoutsLoading}
              onWorkoutClick={handleWorkoutClick}
              onMultipleWorkoutsClick={handleMultipleWorkoutsClick}
              onHourClick={handleHourClick}
            />
          </div>

          {/* Быстрые действия */}
          <div className="mt-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <button 
                onClick={() => {
                  setPreSelectedWorkoutDate(undefined);
                  setIsCreateWorkoutModalOpen(true);
                }}
                className="relative block w-full bg-white p-6 border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    📅 Запланировать тренировку
                  </span>
                </div>
                <span className="block text-sm text-gray-500 mt-1">
                  Создать новую тренировку
                </span>
              </button>

              <button
                onClick={() => setIsCreateClientModalOpen(true)}
                className="relative block w-full bg-white p-6 border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    📝 Добавить клиента
                  </span>
                </div>
                <span className="block text-sm text-gray-500 mt-1">
                  Создать профиль нового клиента
                </span>
              </button>

              <button 
                onClick={() => setIsCreateExerciseModalOpen(true)}
                className="relative block w-full bg-white p-6 border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    💪 Создать упражнение
                  </span>
                </div>
                <span className="block text-sm text-gray-500 mt-1">
                  Добавить новое упражнение
                </span>
              </button>
            </div>
          </div>

          {/* Статистика */}
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div 
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={handleClientsClick}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold text-sm">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Всего клиентов
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {isLoading ? '...' : totalClients}
                        </dd>
                      </dl>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                </div>
              </div>
            </div>

            <div 
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={handleExercisesClick}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold text-sm">💪</span>
                      </div>
                    </div>
                    <div className="ml-5 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Упражнений
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {isLoading ? '...' : totalExercises}
                        </dd>
                      </dl>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                </div>
              </div>
            </div>

            <div 
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={handleWorkoutsClick}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold text-sm">📅</span>
                      </div>
                    </div>
                    <div className="ml-5 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Тренировок
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {isLoading ? '...' : totalWorkouts}
                        </dd>
                      </dl>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                </div>
              </div>
            </div>

            <div 
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={handleCompletedWorkoutsClick}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold text-sm">✅</span>
                      </div>
                    </div>
                    <div className="ml-5 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Завершено
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {isLoading ? '...' : completedWorkouts}
                        </dd>
                      </dl>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                </div>
              </div>
            </div>
          </div>

          {/* Информация о тренере */}
          <div className="mt-8">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Информация о профиле
                </h3>
                <div className="mt-5">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="mt-1 text-sm text-gray-900">{trainer?.email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Телефон</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {trainer?.phone ? formatPhoneDisplay(trainer.phone) : 'Не указан'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Статус</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          trainer?.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {trainer?.is_active ? 'Активен' : 'Неактивен'}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Дата регистрации</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {trainer?.created_at 
                          ? new Date(trainer.created_at).toLocaleDateString('ru-RU')
                          : 'Не указана'
                        }
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальные окна */}
      <CreateExerciseModal
        isOpen={isCreateExerciseModalOpen}
        onClose={() => setIsCreateExerciseModalOpen(false)}
        onSubmit={handleCreateExercise}
        isLoading={isCreateLoading}
      />
      <CreateClientModal
        isOpen={isCreateClientModalOpen}
        onClose={() => setIsCreateClientModalOpen(false)}
        onSubmit={handleCreateClient}
        isLoading={isCreateClientLoading}
      />
      <CreateWorkoutModal
        isOpen={isCreateWorkoutModalOpen}
        onClose={handleCloseCreateWorkoutModal}
        onSubmit={handleCreateWorkout}
        isLoading={isCreateWorkoutLoading}
        preSelectedDate={preSelectedWorkoutDate}
      />
    </div>
  );
};

export default DashboardPage; 